import { Component, ChangeDetectionStrategy, ElementRef, computed, inject } from '@angular/core';
import { IrisFilesService } from '@iris/common/modules/dms/services/files.service';
import { DmsGridTemplateBase } from '../dms-grid-template-base';
import { IrisDmsFoldersSandbox } from '@iris/common/modules/dms/sandbox/dms-folders.sandbox';
import { IrisDmsFilesSandbox } from '@iris/common/modules/dms/sandbox/dms-files.sandbox';
import { IrisInfrastructureService } from '@iris/common/services/infrastructure.service';
import { platformIsMac } from '@iris/common/utils/dom.utils';
import { IrisDMSFileI } from '@iris/common/modules/dms/models/IrisDMSFile';
import { IrisDMSFolderOrFile, isFile } from '@iris/modules/dms/utils/file.utils';
import { ICellRendererParams } from 'ag-grid-community';
import { IrisDmsModalsSandbox } from '@iris/common/modules/dms/sandbox/dms-modals.sandbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IrisDragAndDropDirective } from '@iris/common/modules/directives-common/directives/drag-and-drop.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { IrisFolderSvgComponent } from '@iris/common/modules/dms/components/icons/folder-svg/folder-svg.component';
import { NgClass } from '@angular/common';
import { StrakitButtonComponent } from '@ngx-strakit/cdk/controls';

@Component({
  selector: 'iris-dms-grid-name-template',
  imports: [
    NgClass,
    TranslatePipe,
    MatTooltipModule,

    IrisFolderSvgComponent,
    IrisDragAndDropDirective,
    StrakitButtonComponent,
  ],
  templateUrl: './dms-grid-name-template.component.html',
  styleUrls: ['./dms-grid-name-template.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IrisDmsGridNameTemplateComponent extends DmsGridTemplateBase {
  private readonly filesService = inject(IrisFilesService);
  private readonly dmsFoldersSandbox = inject(IrisDmsFoldersSandbox);
  private readonly dmsFilesSandbox = inject(IrisDmsFilesSandbox);
  private readonly dmsModalsSandbox = inject(IrisDmsModalsSandbox);
  private readonly infrastructureService = inject(IrisInfrastructureService);
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly isFilterActive = this.dmsFilesSandbox.isFilterActive;
  private readonly isRecentFilesFolderSelected = this.dmsFoldersSandbox.isRecentFilesFolderSelected;
  private readonly selectedEntitiesIds = this.dmsFilesSandbox.selectedEntitiesIds;

  readonly shouldShowActionGoToFolder = computed(() => this.isFilterActive() || this.isRecentFilesFolderSelected());

  readonly dragData = computed(() => {
    const selectedEntitiesIds = this.selectedEntitiesIds();
    if (selectedEntitiesIds.length) {
      return { entitiesIds: selectedEntitiesIds };
    }
    const entityId = this.data()?.id;
    return { entitiesIds: entityId ? [entityId] : [] };
  });

  get dropData(): Record<string, string> {
    return { folderId: this.folder()?.id };
  }

  readonly previewSize = 24;

  public nonDraggableElement: HTMLElement;

  public handlerElements: HTMLElement[] = [];

  public isDroppable = false;

  public isDraggable = false;

  context(): Record<string, HTMLElement & ((value: unknown) => unknown)> {
    return this.params()?.context as Record<string, HTMLElement & (() => void)>;
  }

  draggingPicture(): HTMLElement {
    return this.hostElement.nativeElement;
  }

  get isFileLinked(): boolean {
    return !!this.file()?.forms?.length || this.file()?.hasLinks;
  }

  get fileLinksTooltip(): string {
    if (this.file()?.forms?.length) {
      return 'text.dms.FileFormsLinkedInfo';
    }
    return 'text.dms.FileLinkedInfo';
  }

  agInit(params: ICellRendererParams<IrisDMSFolderOrFile>): void {
    super.agInit(params);
    this.initHandlerElements();
    this.setIsDroppable();
    this.setIsDraggable();
  }

  setIsDroppable(): void {
    const data = this.data();
    this.isDroppable = !!data && !isFile(data);
  }

  setIsDraggable(): void {
    const data = this.data();
    this.isDraggable = !!data && !data.isLocked && !this.folder()?.isProjectFolder;
  }

  showFilePreview(event: MouseEvent): void {
    const isMac = platformIsMac();

    if (event.metaKey && isMac || event.ctrlKey && !isMac || event.shiftKey) {
      return;
    }

    this.dmsFilesSandbox.selectEntities([this.file()?.id]);
    this.showFilePreviewModal(this.file());
  }

  private showFilePreviewModal(file: IrisDMSFileI): void {
    return this.dmsModalsSandbox.showFilePreviewModal(file, undefined, true);
  }

  selectFolder(): void {
    this.dmsFoldersSandbox.setSelectedFolder(this.folder()?.id);
    if (this.folder()?.hasChildren) {
      this.dmsFoldersSandbox.expandFolder(this.folder()?.id, this.folder()?.isSystem);
    }
  }

  fileInParentFolderLink(): string {
    return this.filesService.generateSelectFileLink(this.file());
  }

  openFileInParentFolder(event?: MouseEvent): void {
    event?.stopPropagation();

    const url = this.fileInParentFolderLink();
    if (!url) {
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  }

  fileIcon(): string {
    return this.filesService.getIconClass(this.file());
  }

  isOfficeEditableDocument(): boolean {
    return this.dmsFilesSandbox.isFileEditable(this.file());
  }

  nameTooltip(): string {
    return this.isOfficeEditableDocument() ? 'label.dms.OpenInMicrosoftOfficeOnline' : 'label.dms.OpenPreview';
  }

  initHandlerElements(): void {
    const rowId = this.node()?.id;
    const gridNativeElement = this.context()?.gridNativeElement as HTMLElement;
    this.handlerElements = Array.from(gridNativeElement?.querySelectorAll(`[row-id="${rowId}"]`) ?? []);
  }

  onMouseDown(event: MouseEvent): void {
    if (event.detail === 2) {
      document.body.style.cursor = 'default';
      return this.onDblClick(event);
    }
    if (this.data()?.isLocked) {
      event.preventDefault();
      document.body.style.cursor = 'no-drop';
      this.nonDraggableElement = event.target as HTMLElement;
      this.nonDraggableElement.style.cursor = 'no-drop';
      this.nonDraggableElement.parentElement.style.cursor = 'no-drop';
      document.body.addEventListener('mouseup', this.onMouseUp.bind(this));
    }
  }

  onMouseUp(): void {
    document.body.style.cursor = 'default';
    this.nonDraggableElement.style.cursor = 'pointer';
    this.nonDraggableElement.parentElement.style.cursor = 'pointer';
    document.body.removeEventListener('mouseup', this.onMouseUp);
  }

  onDblClick(event: MouseEvent): void {
    event.stopImmediatePropagation();
    if (this.folder()) { return this.selectFolder(); }
    if (this.isOfficeEditableDocument()) {
      this.dmsModalsSandbox.showOfficeEditorModal(this.file());
    } else {
      this.showFilePreview(event);
    }
  }

  onClick(event: MouseEvent): void {
    if (this.infrastructureService.isTablet) {
      this.onDblClick(event);
    }
  }

  dragStarted(): void {
    this.context()?.onDrag(true);
  }

  dragEnded(): void {
    this.context()?.onDrag(false);
  }

  dataDropped(event: DragEvent): void {
    this.context()?.dataDropped(event);
  }
}
